import { type ReactNode, useId } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { FormContainer } from '@/components/FormContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import type { DocumentViewModel } from '@/features/docs/DocumentViewModel'
import { observer } from 'mobx-react-lite'
import { ActionCard } from '@/features/jobs/details/components/ActionCard'
import {
  Card,
  CardContent,
  CardContentDecoration,
  CardContentFooterActions,
  CardItemIcon,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { Icon } from '@/components/Icon'
import { StyledLink } from '@/components/Link'
import { ScrolledAction } from '@/features/jobs/details/components/PageHeader'
import { AutoSkeleton, IsSkeletonContext } from '@/components/Skeleton'
import { dateFormatters } from '@/utils/date-util'
import styles from './DraftReview.module.css'

export interface ReviewDraftDialogProps {
  children?: ReactNode
  isOpen: boolean
  onClose: () => unknown
  onConfirmApprove: () => unknown
  onConfirmReject: () => unknown
  hasMultipleDraftReturns?: boolean
  testId?: string
  approveDraftButtonTestId?: string
  rejectDraftButtonTestId?: string
  notNowButtonTestId?: string
}

/**
 * Dialog for reviewing a draft.
 */
export function ReviewDraftDialog({
  children,
  isOpen,
  onClose,
  onConfirmApprove,
  onConfirmReject,
  hasMultipleDraftReturns,
  testId,
  approveDraftButtonTestId,
  rejectDraftButtonTestId,
  notNowButtonTestId,
}: ReviewDraftDialogProps) {
  return (
    <ModalDialog isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>
          {hasMultipleDraftReturns
            ? 'Review your drafts'
            : 'Review your latest draft'}
        </ModalDialogTitle>
        <ModalDialogDescription>
          Review your return details carefully. Confirm important information
          like your name, address, Social Security number, and dependents are
          correct.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Approve draft"
            variant="default"
            onPress={onConfirmApprove}
            testId={approveDraftButtonTestId}
          />
          <Button
            label="Request changes"
            variant="outline"
            onPress={onConfirmReject}
            testId={rejectDraftButtonTestId}
          />
          <Button
            label="Not now"
            variant="text"
            onPress={onClose}
            testId={notNowButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ConfirmDraftApprovalDialogProps {
  isOpen: boolean
  isPending: boolean
  onClose: () => unknown
  onConfirm: () => unknown
  testId?: string
  confirmButtonTestId?: string
  goBackButtonTestId?: string
}

export function ConfirmDraftApprovalDialog({
  isOpen,
  isPending,
  onClose,
  onConfirm,
  testId,
  confirmButtonTestId,
  goBackButtonTestId,
}: ConfirmDraftApprovalDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Are you sure that you approve of this draft?
        </ModalDialogTitle>
        <ModalDialogDescription>
          Once accepted, the Pro will be able to finalize and submit your
          return.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, I approve"
            variant="default"
            onPress={onConfirm}
            isPending={isPending}
            testId={confirmButtonTestId}
          />
          <Button
            label="Go back"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
            testId={goBackButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface RequestChangesDialogProps {
  children?: ReactNode
  isOpen: boolean
  isPending: boolean
  canSubmit: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  testId?: string
  submitButtonTestId?: string
  goBackButtonTestId?: string
}

export function RequestChangesDialog({
  children,
  isOpen,
  isPending,
  canSubmit,
  onClose,
  onSubmit,
  testId,
  submitButtonTestId,
  goBackButtonTestId,
}: RequestChangesDialogProps) {
  const formId = useId()

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>What needs to change?</ModalDialogTitle>
        <ModalDialogDescription>
          Let your Pro know what to update. We'll send this to them as a
          message.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent spacing="compact">
        <FormContainer id={formId} onSubmit={onSubmit}>
          {children}
        </FormContainer>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Send request"
            variant="default"
            type="submit"
            form={formId}
            isDisabled={!canSubmit}
            isPending={isPending}
            testId={submitButtonTestId}
          />
          <Button
            label="Go back"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
            testId={goBackButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface DraftReturnRowProps {
  title: string
  uploadedBy?: string
  uploadedAt: Date
  onView: () => unknown
  onDownload: () => unknown
  testId?: string
}

export function DraftReturnRow({
  title,
  uploadedBy,
  uploadedAt,
  onView,
  onDownload,
  testId,
}: DraftReturnRowProps) {
  return (
    <div className={styles.draftRow} data-test={testId}>
      <CardItemIcon name="file" variant="surface-background" />
      <div className={styles.draftRowText}>
        <p className={styles.draftRowTitle}>{title}</p>
        <p className={styles.draftRowSubtitle}>
          Uploaded by {uploadedBy} on{' '}
          {dateFormatters.dateTimeDefault.format(uploadedAt)}
        </p>
      </div>
      <div className={styles.draftRowActions}>
        <StyledLink onPress={onView}>View</StyledLink>
        <span aria-hidden className={styles.draftRowActionsDivider}>
          |
        </span>
        <StyledLink onPress={onDownload}>Download</StyledLink>
      </div>
    </div>
  )
}

interface DraftReturnRowContainerProps {
  draftReturnVm: DocumentViewModel
}

const DraftReturnRowContainer = observer(function DraftReturnRowContainer({
  draftReturnVm,
}: DraftReturnRowContainerProps) {
  return (
    <DraftReturnRow
      title={draftReturnVm.doc.title}
      uploadedBy={draftReturnVm.author?.displayName}
      uploadedAt={draftReturnVm.doc.dateCreated}
      onView={() => draftReturnVm.preview()}
      onDownload={() => draftReturnVm.download()}
      testId={`review-draft-dialog-document-${draftReturnVm.doc.longId}`}
    />
  )
})

interface ReviewDraftDialogContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Review Draft Dialog Container
 */
export const ReviewDraftDialogContainer = observer(
  function ReviewDraftDialogContainer({
    vm: { progressionVm },
  }: ReviewDraftDialogContainerProps) {
    const isApproving = progressionVm.draftReviewVm.approveDraft.pending
    const isRequestingChanges =
      progressionVm.draftReviewVm.submitRequestedChanges.pending

    const draftReturnVms = progressionVm.draftReviewVm.draftReturnVms
    const isFetchingDraftReturns =
      progressionVm.draftReviewVm.fetchDraftReturn.pending
    const hasMultipleDraftReturns = draftReturnVms.length > 1
    const singleDraftReturnVm = draftReturnVms[0]
    return (
      <>
        <ReviewDraftDialog
          isOpen={progressionVm.draftReviewVm.modal.showing}
          onClose={progressionVm.draftReviewVm.modal.close}
          onConfirmApprove={
            progressionVm.draftReviewVm.confirmDraftApprovalModalVm.show
          }
          onConfirmReject={
            progressionVm.draftReviewVm.confirmDraftRejectionModalVm.show
          }
          hasMultipleDraftReturns={hasMultipleDraftReturns}
          testId="review-draft-dialog"
          approveDraftButtonTestId="review-draft-dialog-approve-button"
          rejectDraftButtonTestId="review-draft-dialog-reject-button"
          notNowButtonTestId="review-draft-dialog-not-now-button"
        >
          {hasMultipleDraftReturns ? (
            <Card>
              {draftReturnVms.map((draftReturnVm) => (
                <DraftReturnRowContainer
                  key={draftReturnVm.doc.longId}
                  draftReturnVm={draftReturnVm}
                />
              ))}
            </Card>
          ) : isFetchingDraftReturns || singleDraftReturnVm ? (
            <Card>
              <CardContent>
                <IsSkeletonContext value={isFetchingDraftReturns}>
                  <CardContentDecoration>
                    <Icon name="file" variant="surface-background" />
                  </CardContentDecoration>

                  <CardTitle>
                    <AutoSkeleton>Latest draft return</AutoSkeleton>
                  </CardTitle>
                  <CardSubtitle>
                    <AutoSkeleton>
                      Uploaded by {singleDraftReturnVm?.author?.displayName} on{' '}
                      {dateFormatters.dateTimeDefault.format(
                        singleDraftReturnVm?.doc.dateCreated,
                      )}
                    </AutoSkeleton>
                  </CardSubtitle>
                  <CardContentFooterActions>
                    <StyledLink onPress={() => singleDraftReturnVm?.preview()}>
                      <AutoSkeleton>View</AutoSkeleton>
                    </StyledLink>
                    <StyledLink onPress={() => singleDraftReturnVm?.download()}>
                      <AutoSkeleton>Download</AutoSkeleton>
                    </StyledLink>
                  </CardContentFooterActions>
                </IsSkeletonContext>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <CardContentDecoration>
                  <Icon name="alert" variant="surface-background" />
                </CardContentDecoration>
                <CardTitle>No draft return document found</CardTitle>
                <CardSubtitle>
                  It doesn't look like you have a draft return for this job to
                  review yet. Please contact your Pro if you think this is an
                  error.
                </CardSubtitle>
              </CardContent>
            </Card>
          )}
        </ReviewDraftDialog>

        <ConfirmDraftApprovalDialog
          isOpen={
            progressionVm.draftReviewVm.confirmDraftApprovalModalVm.showing
          }
          onClose={
            progressionVm.draftReviewVm.confirmDraftApprovalModalVm.close
          }
          onConfirm={progressionVm.draftReviewVm.approveDraft}
          isPending={isApproving}
          testId="confirm-draft-approval-dialog"
          confirmButtonTestId="confirm-draft-approval-dialog-confirm-button"
          goBackButtonTestId="confirm-draft-approval-dialog-go-back-button"
        />

        <RequestChangesDialog
          isOpen={
            progressionVm.draftReviewVm.confirmDraftRejectionModalVm.showing
          }
          onClose={progressionVm.draftReviewVm.cancelRequestChanges}
          onSubmit={progressionVm.draftReviewVm.submitRequestedChanges}
          canSubmit={progressionVm.draftReviewVm.canSubmitRequestedChanges}
          isPending={isRequestingChanges}
          testId="confirm-draft-rejection-dialog"
          submitButtonTestId="confirm-draft-rejection-dialog-submit-button"
          goBackButtonTestId="confirm-draft-rejection-dialog-go-back-button"
        >
          <TextAreaContainer
            rows={4}
            model={progressionVm.draftReviewVm}
            field="requestedChanges"
            label="Requested changes"
            name="requestedChanges"
            placeholder="Describe the changes you need to your draft."
            testId="confirm-draft-rejection-dialog-changes-input"
          />
        </RequestChangesDialog>
      </>
    )
  },
)

interface ReviewDraftActionCardContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Container for the action card + dialog for reviewing a draft.
 */
export const ReviewDraftActionCardContainer = observer(
  function ReviewDraftDialogContainer({
    vm,
  }: ReviewDraftActionCardContainerProps) {
    return (
      <>
        <ActionCard kicker="Your draft is ready" title="Pending your review">
          <Button
            onPress={vm.progressionVm.draftReviewVm.reviewDraft}
            label="Review your draft"
            testId="review-draft-button"
          />
        </ActionCard>
      </>
    )
  },
)

interface ReviewDraftScrolledActionContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Scrolled action for the header.
 */
export const ReviewDraftScrolledActionContainer = observer(
  function ReviewDraftScrolledActionContainer({
    vm,
  }: ReviewDraftScrolledActionContainerProps) {
    return (
      <ScrolledAction
        label="Review your draft"
        onPress={vm.progressionVm.draftReviewVm.reviewDraft}
      />
    )
  },
)
